/**
 * Rich interactive templates for AVI Web IDE
 */

export interface TemplateResult {
    appTitle: string;
    projectName: string;
    files: { path: string; content: string }[];
}

export function getTemplateFiles(userPrompt: string): TemplateResult {
    const p = userPrompt.toLowerCase();
    const isEcommerce = /\b(ecommerce|e-commerce|store|shop|shopping|cart|checkout|products|buy|catalog|market|retail)\b/i.test(p);
    const isLogin = /\b(login|auth|signin|signup|sign in|sign up|register|credentials|password)\b/i.test(p);
    const isCalculator = /\b(calc|clc|calculator|clculator|calcutor|calculater|math)\b/i.test(p) || p.includes('calc') || p.includes('clc');
    const isGame = /\b(game|arcade|frogger|retro|play|canvas game|platformer|pong|space invader|flappy|runner|video game)\b/i.test(p);

    if (isGame) {
        return getGameTemplate();
    } else if (isEcommerce) {
        return getEcommerceTemplate();
    } else if (isLogin) {
        return getLoginTemplate();
    } else if (isCalculator) {
        return getCalculatorTemplate();
    } else {
        return getGenericAppTemplate(userPrompt);
    }
}

function getEcommerceTemplate(): TemplateResult {
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>CyberStore - Next-Gen Electronics</title>
    <link rel="stylesheet" href="styles.css">
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
</head>
<body>
    <header class="navbar">
        <div class="nav-brand">
            <i class="fas fa-bolt brand-icon"></i>
            <span>Cyber<strong>Store</strong></span>
        </div>
        <div class="search-bar">
            <i class="fas fa-search"></i>
            <input type="text" id="searchInput" placeholder="Search gadgets, audio, gear...">
        </div>
        <div class="nav-actions">
            <button class="cart-trigger" id="cartTriggerBtn">
                <i class="fas fa-shopping-bag"></i>
                <span class="cart-badge" id="cartBadge">0</span>
            </button>
        </div>
    </header>

    <div class="hero-banner">
        <div class="hero-content">
            <span class="promo-tag"><i class="fas fa-sparkles"></i> 20% OFF SUMMER DROP</span>
            <h1>Engineered for the Modern Creator</h1>
            <p>High-performance computing accessories, audiophile wireless sound, and ergonomic gear.</p>
        </div>
    </div>

    <main class="store-container">
        <div class="filter-pills">
            <button class="pill active" data-category="all">All Products</button>
            <button class="pill" data-category="Audio">Audio</button>
            <button class="pill" data-category="Keyboards">Keyboards</button>
            <button class="pill" data-category="Displays">Displays</button>
            <button class="pill" data-category="Gear">Gear</button>
        </div>

        <div class="products-grid" id="productsGrid">
            <!-- Injected by app.js -->
        </div>
    </main>

    <!-- Slide-out Cart Drawer -->
    <div class="cart-overlay" id="cartOverlay"></div>
    <aside class="cart-drawer" id="cartDrawer">
        <div class="cart-header">
            <h3><i class="fas fa-bag-shopping"></i> Your Cart (<span id="cartCountHeader">0</span>)</h3>
            <button class="close-cart-btn" id="closeCartBtn"><i class="fas fa-xmark"></i></button>
        </div>
        <div class="cart-items" id="cartItemsList">
            <!-- Injected by app.js -->
        </div>
        <div class="cart-footer">
            <div class="summary-line">
                <span>Subtotal</span>
                <span id="subtotalAmount">$0.00</span>
            </div>
            <div class="summary-line">
                <span>Shipping</span>
                <span class="free-shipping">FREE</span>
            </div>
            <div class="summary-line total">
                <span>Total</span>
                <span id="totalAmount">$0.00</span>
            </div>
            <button class="btn-checkout" id="checkoutBtn">
                <i class="fas fa-lock"></i> Proceed to Checkout
            </button>
        </div>
    </aside>

    <!-- Checkout Modal -->
    <div class="modal-overlay" id="checkoutModal">
        <div class="modal-card">
            <div class="modal-header">
                <h3><i class="fas fa-shield-check"></i> Secure Checkout</h3>
                <button class="close-modal-btn" id="closeModalBtn"><i class="fas fa-xmark"></i></button>
            </div>
            <form id="checkoutForm" onsubmit="event.preventDefault(); window.completeOrder();">
                <div class="form-section">
                    <label>Shipping Details</label>
                    <input type="text" placeholder="Full Name" value="Alex Rivera" required>
                    <input type="email" placeholder="Email Address" value="alex.rivera@example.com" required>
                    <input type="text" placeholder="Street Address" value="742 Evergreen Terrace" required>
                    <div class="form-row">
                        <input type="text" placeholder="City" value="San Francisco" required>
                        <input type="text" placeholder="ZIP Code" value="94107" required>
                    </div>
                </div>
                <div class="form-section">
                    <label>Payment Information (Demo)</label>
                    <div class="card-input-wrapper">
                        <i class="fas fa-credit-card"></i>
                        <input type="text" placeholder="Card Number" value="4242 •••• •••• 4242" required>
                    </div>
                    <div class="form-row">
                        <input type="text" placeholder="MM/YY" value="12/28" required>
                        <input type="text" placeholder="CVC" value="842" required>
                    </div>
                </div>
                <button type="submit" class="btn-place-order" id="placeOrderBtn">
                    Complete Order (<span id="modalTotal">$0.00</span>)
                </button>
            </form>
        </div>
    </div>

    <!-- Success Celebration Modal -->
    <div class="modal-overlay" id="successModal">
        <div class="modal-card success-card">
            <div class="success-icon"><i class="fas fa-circle-check"></i></div>
            <h2>Order Confirmed!</h2>
            <p>Thank you for shopping with CyberStore. Your gear is being prepared for immediate dispatch.</p>
            <div class="order-receipt">
                <div><span>Order Number:</span> <strong id="orderNumberDisplay">#ORD-94810</strong></div>
                <div><span>Estimated Delivery:</span> <strong>2 Business Days</strong></div>
            </div>
            <button class="btn-continue" id="continueShoppingBtn">Continue Shopping</button>
        </div>
    </div>

    <script src="app.js"></script>
</body>
</html>`;

    const css = `:root {
    --bg-dark: #080c14;
    --card-bg: rgba(20, 27, 45, 0.7);
    --card-border: rgba(255, 255, 255, 0.08);
    --primary: #6366f1;
    --primary-glow: rgba(99, 102, 241, 0.35);
    --accent: #38bdf8;
    --success: #10b981;
    --text-main: #f8fafc;
    --text-muted: #94a3b8;
}

* { box-sizing: border-box; margin: 0; padding: 0; font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
body { background: var(--bg-dark); color: var(--text-main); min-height: 100vh; overflow-x: hidden; }

.navbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 28px;
    background: rgba(12, 18, 30, 0.8);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid var(--card-border);
    position: sticky;
    top: 0;
    z-index: 100;
}
.nav-brand { display: flex; align-items: center; gap: 10px; font-size: 1.25rem; font-weight: 700; }
.brand-icon { color: var(--primary); font-size: 1.4rem; }
.nav-brand strong { color: var(--accent); }

.search-bar { position: relative; width: 100%; max-width: 380px; margin: 0 20px; }
.search-bar i { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: var(--text-muted); }
.search-bar input {
    width: 100%; padding: 10px 14px 10px 40px;
    background: rgba(255, 255, 255, 0.05); border: 1px solid var(--card-border);
    border-radius: 12px; color: var(--text-main); font-size: 0.9rem; outline: none;
}
.search-bar input:focus { border-color: var(--primary); background: rgba(255, 255, 255, 0.08); }

.cart-trigger {
    background: rgba(99, 102, 241, 0.15); border: 1px solid rgba(99, 102, 241, 0.3);
    color: #fff; padding: 10px 18px; border-radius: 12px; cursor: pointer;
    font-size: 1.1rem; position: relative; display: flex; align-items: center; gap: 8px;
}
.cart-trigger:hover { background: var(--primary); transform: translateY(-1px); }
.cart-badge { background: #ef4444; color: #fff; font-size: 0.75rem; font-weight: 700; padding: 2px 7px; border-radius: 20px; }

.hero-banner {
    padding: 48px 28px 24px; text-align: center;
    background: radial-gradient(circle at 50% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 60%);
}
.promo-tag {
    display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px;
    background: rgba(56, 189, 248, 0.12); border: 1px solid rgba(56, 189, 248, 0.3);
    color: var(--accent); border-radius: 30px; font-size: 0.75rem; font-weight: 700; margin-bottom: 14px;
}
.hero-banner h1 { font-size: 2.2rem; font-weight: 800; margin-bottom: 10px; }
.hero-banner p { color: var(--text-muted); font-size: 1rem; max-width: 580px; margin: 0 auto; }

.store-container { max-width: 1200px; margin: 0 auto; padding: 24px 28px 60px; }
.filter-pills { display: flex; gap: 10px; margin-bottom: 28px; overflow-x: auto; padding-bottom: 6px; }
.pill {
    background: rgba(255, 255, 255, 0.04); border: 1px solid var(--card-border);
    color: var(--text-muted); padding: 8px 18px; border-radius: 20px;
    font-weight: 600; font-size: 0.85rem; cursor: pointer; transition: all 0.2s; white-space: nowrap;
}
.pill:hover, .pill.active { background: var(--primary); color: #fff; border-color: var(--primary); }

.products-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 24px; }
.product-card {
    background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 18px;
    overflow: hidden; display: flex; flex-direction: column; transition: transform 0.25s, border-color 0.25s;
}
.product-card:hover { transform: translateY(-4px); border-color: rgba(99, 102, 241, 0.4); }
.product-img-wrap { height: 180px; background: #0f1523; overflow: hidden; position: relative; }
.product-img { width: 100%; height: 100%; object-fit: cover; }
.product-badge { position: absolute; top: 12px; left: 12px; background: rgba(15, 23, 42, 0.85); color: var(--accent); padding: 4px 10px; border-radius: 8px; font-size: 0.72rem; font-weight: 700; }
.product-body { padding: 18px; display: flex; flex-direction: column; flex: 1; }
.product-title { font-size: 1rem; font-weight: 700; margin-bottom: 6px; }
.product-desc { font-size: 0.82rem; color: var(--text-muted); margin-bottom: 14px; flex: 1; line-height: 1.4; }
.product-meta { display: flex; justify-content: space-between; align-items: center; margin-top: auto; }
.product-price { font-size: 1.25rem; font-weight: 800; color: #fff; }
.btn-add-cart {
    background: var(--primary); border: none; color: #fff; padding: 8px 16px; border-radius: 10px;
    font-weight: 600; font-size: 0.85rem; cursor: pointer; display: flex; align-items: center; gap: 6px;
}
.btn-add-cart:hover { background: #4f46e5; }

.cart-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(4px); z-index: 200; opacity: 0; pointer-events: none; transition: opacity 0.3s; }
.cart-overlay.open { opacity: 1; pointer-events: auto; }
.cart-drawer { position: fixed; top: 0; right: 0; width: 100%; max-width: 420px; height: 100vh; background: #0f1626; border-left: 1px solid var(--card-border); z-index: 201; transform: translateX(100%); transition: transform 0.3s ease; display: flex; flex-direction: column; }
.cart-drawer.open { transform: translateX(0); }
.cart-header { padding: 20px 24px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--card-border); }
.close-cart-btn { background: none; border: none; color: var(--text-muted); font-size: 1.2rem; cursor: pointer; }
.cart-items { flex: 1; overflow-y: auto; padding: 20px 24px; display: flex; flex-direction: column; gap: 14px; }
.cart-item { display: flex; gap: 14px; align-items: center; padding: 12px; background: rgba(255, 255, 255, 0.03); border: 1px solid var(--card-border); border-radius: 14px; }
.cart-item img { width: 56px; height: 56px; border-radius: 8px; object-fit: cover; }
.cart-item-info { flex: 1; }
.cart-item-info h4 { font-size: 0.9rem; font-weight: 600; }
.cart-item-info p { color: var(--accent); font-weight: 700; font-size: 0.85rem; margin-top: 2px; }
.qty-controls { display: flex; align-items: center; gap: 8px; }
.qty-btn { background: rgba(255, 255, 255, 0.08); border: none; color: #fff; width: 24px; height: 24px; border-radius: 6px; cursor: pointer; }
.cart-footer { padding: 20px 24px; border-top: 1px solid var(--card-border); background: rgba(12, 18, 30, 0.8); }
.summary-line { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 0.9rem; color: var(--text-muted); }
.summary-line.total { font-size: 1.15rem; font-weight: 800; color: #fff; margin-top: 10px; border-top: 1px dashed var(--card-border); padding-top: 10px; }
.free-shipping { color: var(--success); font-weight: 700; }
.btn-checkout { width: 100%; margin-top: 16px; padding: 14px; background: linear-gradient(135deg, var(--primary), #8b5cf6); border: none; border-radius: 12px; color: #fff; font-weight: 700; font-size: 1rem; cursor: pointer; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); backdrop-filter: blur(6px); z-index: 300; display: none; align-items: center; justify-content: center; padding: 20px; }
.modal-overlay.open { display: flex; }
.modal-card { background: #0f1628; border: 1px solid var(--card-border); border-radius: 22px; width: 100%; max-width: 480px; padding: 28px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.close-modal-btn { background: none; border: none; color: var(--text-muted); font-size: 1.2rem; cursor: pointer; }
.form-section { margin-bottom: 18px; }
.form-section label { display: block; font-size: 0.78rem; font-weight: 700; text-transform: uppercase; color: var(--accent); margin-bottom: 8px; }
.form-section input { width: 100%; padding: 10px 14px; background: rgba(255, 255, 255, 0.04); border: 1px solid var(--card-border); border-radius: 10px; color: #fff; margin-bottom: 8px; font-size: 0.88rem; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.card-input-wrapper { position: relative; }
.card-input-wrapper i { position: absolute; left: 14px; top: 13px; color: var(--text-muted); }
.card-input-wrapper input { padding-left: 40px; }
.btn-place-order { width: 100%; padding: 14px; background: var(--success); border: none; border-radius: 12px; color: #fff; font-weight: 700; font-size: 1rem; cursor: pointer; }
.success-card { text-align: center; padding: 40px 28px; }
.success-icon { font-size: 3.5rem; color: var(--success); margin-bottom: 16px; }
.order-receipt { background: rgba(255, 255, 255, 0.03); border: 1px solid var(--card-border); border-radius: 12px; padding: 14px 18px; margin: 20px 0; display: flex; flex-direction: column; gap: 8px; font-size: 0.9rem; text-align: left; }
.order-receipt div { display: flex; justify-content: space-between; }
.btn-continue { width: 100%; padding: 12px; background: var(--primary); border: none; border-radius: 10px; color: #fff; font-weight: 700; cursor: pointer; }`;

    const js = `// CyberStore Interactive Engine
const products = [
    { id: 1, title: "CyberPulse ANC Headphones", category: "Audio", price: 189.99, badge: "Best Seller", description: "Studio-grade lossless wireless sound with hybrid 45dB noise cancellation.", image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=500&auto=format&fit=crop&q=60" },
    { id: 2, title: "Vortex Pro Mechanical Keyboard", category: "Keyboards", price: 139.50, badge: "New Release", description: "Hot-swappable tactile linear switches with customizable per-key RGB backlighting.", image: "https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=500&auto=format&fit=crop&q=60" },
    { id: 3, title: "UltraVision 34\\" Curved OLED", category: "Displays", price: 499.00, badge: "Top Pick", description: "165Hz ultra-wide curved gaming monitor with 99% DCI-P3 cinematic color.", image: "https://images.unsplash.com/photo-1527443224154-c4a3942d3acf?w=500&auto=format&fit=crop&q=60" },
    { id: 4, title: "AuraGlow Smart Ambient Bar", category: "Gear", price: 49.99, badge: "Trending", description: "Sound-reactive ambient LED light bar with wireless desktop control dial.", image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=500&auto=format&fit=crop&q=60" },
    { id: 5, title: "TitanPro Aluminum Stand", category: "Gear", price: 42.00, badge: "Essential", description: "Precision CNC milled aluminum cooling stand with 360-degree rotation.", image: "https://images.unsplash.com/photo-1544816155-12df9643f363?w=500&auto=format&fit=crop&q=60" },
    { id: 6, title: "SonicBass Studio Earbuds", category: "Audio", price: 89.00, badge: "Popular", description: "Waterproof IPX7 wireless earbuds with 36-hour total playback battery life.", image: "https://images.unsplash.com/photo-1590658268037-6bf12165a8df?w=500&auto=format&fit=crop&q=60" }
];

let cart = [];
let activeCategory = 'all';

function renderProducts(filter = 'all', searchQuery = '') {
    const grid = document.getElementById('productsGrid');
    if (!grid) return;

    let filtered = products;
    if (filter !== 'all') filtered = filtered.filter(p => p.category.toLowerCase() === filter.toLowerCase());
    if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        filtered = filtered.filter(p => p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q));
    }

    if (filtered.length === 0) {
        grid.innerHTML = \`<div style="grid-column: 1/-1; text-align: center; padding: 40px; color: #94a3b8;">No matching products found.</div>\`;
        return;
    }

    grid.innerHTML = filtered.map(p => \`
        <div class="product-card">
            <div class="product-img-wrap">
                <img class="product-img" src="\${p.image}" alt="\${p.title}">
                <span class="product-badge">\${p.badge}</span>
            </div>
            <div class="product-body">
                <h3 class="product-title">\${p.title}</h3>
                <p class="product-desc">\${p.description}</p>
                <div class="product-meta">
                    <span class="product-price">$\${p.price.toFixed(2)}</span>
                    <button class="btn-add-cart" onclick="addToCart(\${p.id})">
                        <i class="fas fa-cart-plus"></i> Add to Cart
                    </button>
                </div>
            </div>
        </div>
    \`).join('');
}

window.addToCart = function(productId) {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const existing = cart.find(item => item.id === productId);
    if (existing) existing.quantity += 1;
    else cart.push({ ...product, quantity: 1 });

    updateCartUI();
    console.log(\`[Store] Added '\${product.title}' to cart. Total items: \${cart.reduce((s, i) => s + i.quantity, 0)}\`);
    openCart();
};

window.changeQuantity = function(productId, delta) {
    const item = cart.find(i => i.id === productId);
    if (!item) return;
    item.quantity += delta;
    if (item.quantity <= 0) cart = cart.filter(i => i.id !== productId);
    updateCartUI();
};

function updateCartUI() {
    const count = cart.reduce((sum, item) => sum + item.quantity, 0);
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    const badge = document.getElementById('cartBadge');
    if (badge) badge.textContent = count;
    const countHeader = document.getElementById('cartCountHeader');
    if (countHeader) countHeader.textContent = count;
    const subtotalEl = document.getElementById('subtotalAmount');
    if (subtotalEl) subtotalEl.textContent = \`$\${subtotal.toFixed(2)}\`;
    const totalEl = document.getElementById('totalAmount');
    if (totalEl) totalEl.textContent = \`$\${subtotal.toFixed(2)}\`;
    const modalTotal = document.getElementById('modalTotal');
    if (modalTotal) modalTotal.textContent = \`$\${subtotal.toFixed(2)}\`;

    const list = document.getElementById('cartItemsList');
    if (list) {
        if (cart.length === 0) {
            list.innerHTML = \`<div style="text-align: center; color: #94a3b8; padding: 40px 0;"><i class="fas fa-shopping-basket" style="font-size: 2.5rem; opacity: 0.3; margin-bottom: 12px; display: block;"></i>Your cart is empty.</div>\`;
        } else {
            list.innerHTML = cart.map(item => \`
                <div class="cart-item">
                    <img src="\${item.image}" alt="\${item.title}">
                    <div class="cart-item-info">
                        <h4>\${item.title}</h4>
                        <p>$\${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                    <div class="qty-controls">
                        <button class="qty-btn" onclick="changeQuantity(\${item.id}, -1)">-</button>
                        <span>\${item.quantity}</span>
                        <button class="qty-btn" onclick="changeQuantity(\${item.id}, 1)">+</button>
                    </div>
                </div>
            \`).join('');
        }
    }
}

function openCart() {
    document.getElementById('cartDrawer')?.classList.add('open');
    document.getElementById('cartOverlay')?.classList.add('open');
}

function closeCart() {
    document.getElementById('cartDrawer')?.classList.remove('open');
    document.getElementById('cartOverlay')?.classList.remove('open');
}

window.completeOrder = function() {
    const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const randomOrderNum = '#ORD-' + Math.floor(10000 + Math.random() * 90000);
    document.getElementById('checkoutModal')?.classList.remove('open');
    const orderDisplay = document.getElementById('orderNumberDisplay');
    if (orderDisplay) orderDisplay.textContent = randomOrderNum;

    document.getElementById('successModal')?.classList.add('open');
    console.log(\`[Store] Order confirmed \${randomOrderNum} for $\${total.toFixed(2)}. Processing fulfillment.\`);
    cart = [];
    updateCartUI();
};

document.getElementById('cartTriggerBtn')?.addEventListener('click', openCart);
document.getElementById('closeCartBtn')?.addEventListener('click', closeCart);
document.getElementById('cartOverlay')?.addEventListener('click', closeCart);

document.getElementById('checkoutBtn')?.addEventListener('click', () => {
    if (cart.length === 0) { alert('Your cart is empty!'); return; }
    closeCart();
    document.getElementById('checkoutModal')?.classList.add('open');
});

document.getElementById('closeModalBtn')?.addEventListener('click', () => {
    document.getElementById('checkoutModal')?.classList.remove('open');
});

document.getElementById('continueShoppingBtn')?.addEventListener('click', () => {
    document.getElementById('successModal')?.classList.remove('open');
});

document.querySelectorAll('.pill').forEach(btn => {
    btn.addEventListener('click', () => {
        document.querySelectorAll('.pill').forEach(p => p.classList.remove('active'));
        btn.classList.add('active');
        activeCategory = btn.dataset.category;
        renderProducts(activeCategory);
    });
});

document.getElementById('searchInput')?.addEventListener('input', (e) => {
    renderProducts(activeCategory, e.target.value);
});

renderProducts();
console.log('[Store] CyberStore initialized with ' + products.length + ' products.');`;

    return {
        appTitle: "CyberStore - Next-Gen Tech",
        projectName: "E-Commerce Store",
        files: [
            { path: 'index.html', content: html },
            { path: 'styles.css', content: css },
            { path: 'app.js', content: js }
        ]
    };
}

function getLoginTemplate(): TemplateResult {
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Authentication</title>
    <link rel="stylesheet" href="styles.css">
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
</head>
<body>
    <div class="auth-card">
        <div class="auth-tabs">
            <button class="tab-btn active" id="tabSignIn">Sign In</button>
            <button class="tab-btn" id="tabSignUp">Sign Up</button>
        </div>
        <div class="auth-body">
            <h2 id="formTitle">Welcome Back</h2>
            <p id="formSub">Please enter your credentials to access your account</p>
            <form id="authForm" onsubmit="event.preventDefault(); window.handleAuth();">
                <div class="input-group" id="nameFieldGroup" style="display: none;">
                    <i class="fas fa-user"></i>
                    <input type="text" id="nameInput" placeholder="Full Name">
                </div>
                <div class="input-group">
                    <i class="fas fa-envelope"></i>
                    <input type="email" id="emailInput" placeholder="Email Address" value="user@example.com" required>
                </div>
                <div class="input-group">
                    <i class="fas fa-lock"></i>
                    <input type="password" id="passwordInput" placeholder="Password" value="••••••••" required>
                    <button type="button" class="btn-toggle-pw" onclick="window.togglePassword()"><i class="fas fa-eye"></i></button>
                </div>
                <div class="form-options">
                    <label class="remember-label"><input type="checkbox" checked> Remember me</label>
                    <a href="#" class="forgot-link">Forgot password?</a>
                </div>
                <button type="submit" class="btn-auth" id="submitBtn">Sign In</button>
            </form>
            <div id="authNotice" class="auth-notice" style="display: none;"></div>
        </div>
    </div>
    <script src="app.js"></script>
</body>
</html>`;

    const css = `* { box-sizing: border-box; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
body {
    background: radial-gradient(circle at 50% 50%, #1e1b4b 0%, #0a0e1a 100%);
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #f8fafc;
}
.auth-card {
    background: rgba(30, 41, 59, 0.65);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    width: 100%;
    max-width: 420px;
    padding: 32px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
}
.auth-tabs { display: flex; gap: 8px; background: rgba(0, 0, 0, 0.25); padding: 4px; border-radius: 12px; margin-bottom: 24px; }
.tab-btn { flex: 1; padding: 10px; background: none; border: none; color: #94a3b8; font-weight: 600; cursor: pointer; border-radius: 8px; transition: all 0.2s; }
.tab-btn.active { background: #6366f1; color: #fff; box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4); }
.auth-body h2 { font-size: 1.6rem; font-weight: 700; margin-bottom: 6px; }
.auth-body p { color: #94a3b8; font-size: 0.88rem; margin-bottom: 20px; }
.input-group { position: relative; margin-bottom: 14px; }
.input-group i { position: absolute; left: 14px; top: 15px; color: #94a3b8; }
.input-group input { width: 100%; padding: 12px 40px; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 12px; color: #fff; font-size: 0.95rem; outline: none; }
.input-group input:focus { border-color: #6366f1; }
.btn-toggle-pw { position: absolute; right: 14px; top: 14px; background: none; border: none; color: #94a3b8; cursor: pointer; }
.form-options { display: flex; justify-content: space-between; font-size: 0.82rem; color: #94a3b8; margin-bottom: 20px; }
.forgot-link { color: #818cf8; text-decoration: none; }
.btn-auth { width: 100%; padding: 14px; background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none; border-radius: 12px; color: #fff; font-size: 1rem; font-weight: 700; cursor: pointer; }
.auth-notice { margin-top: 16px; padding: 10px; background: rgba(34, 197, 94, 0.15); border: 1px solid #22c55e; border-radius: 10px; color: #4ade80; font-size: 0.85rem; text-align: center; }`;

    const js = `let isSignUp = false;
window.handleAuth = function() {
    const email = document.getElementById('emailInput').value;
    const notice = document.getElementById('authNotice');
    notice.style.display = 'block';
    notice.innerHTML = '<i class="fas fa-check-circle"></i> Authenticated successfully as ' + email;
    console.log('[Auth] User signed in successfully: ' + email);
};

window.togglePassword = function() {
    const inp = document.getElementById('passwordInput');
    inp.type = inp.type === 'password' ? 'text' : 'password';
};

document.getElementById('tabSignIn')?.addEventListener('click', () => {
    isSignUp = false;
    document.getElementById('tabSignIn').classList.add('active');
    document.getElementById('tabSignUp').classList.remove('active');
    document.getElementById('formTitle').textContent = 'Welcome Back';
    document.getElementById('submitBtn').textContent = 'Sign In';
    document.getElementById('nameFieldGroup').style.display = 'none';
});

document.getElementById('tabSignUp')?.addEventListener('click', () => {
    isSignUp = true;
    document.getElementById('tabSignUp').classList.add('active');
    document.getElementById('tabSignIn').classList.remove('active');
    document.getElementById('formTitle').textContent = 'Create Account';
    document.getElementById('submitBtn').textContent = 'Sign Up';
    document.getElementById('nameFieldGroup').style.display = 'block';
});

console.log('[Auth] Authentication Suite initialized.');`;

    return {
        appTitle: "Glassmorphic Auth Suite",
        projectName: "Login Screen",
        files: [
            { path: 'index.html', content: html },
            { path: 'styles.css', content: css },
            { path: 'app.js', content: js }
        ]
    };
}

function getCalculatorTemplate(): TemplateResult {
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sleek Calculator</title>
    <link rel="stylesheet" href="styles.css">
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
</head>
<body>
    <div class="calculator-card">
        <div class="calc-header">
            <span class="badge">AVI Calc</span>
            <div class="calc-actions">
                <button id="themeToggle" class="icon-btn" title="Toggle Theme"><i class="fas fa-moon"></i></button>
                <button id="clearHistory" class="icon-btn" title="Clear History"><i class="fas fa-trash-can"></i></button>
            </div>
        </div>
        <div class="display-container">
            <div id="historyDisplay" class="history-display"></div>
            <div id="currentDisplay" class="current-display">0</div>
        </div>
        <div class="keypad">
            <button class="key action" data-action="clear">AC</button>
            <button class="key action" data-action="delete"><i class="fas fa-delete-left"></i></button>
            <button class="key operator" data-op="%">%</button>
            <button class="key operator" data-op="/">÷</button>
            <button class="key number" data-num="7">7</button>
            <button class="key number" data-num="8">8</button>
            <button class="key number" data-num="9">9</button>
            <button class="key operator" data-op="*">×</button>
            <button class="key number" data-num="4">4</button>
            <button class="key number" data-num="5">5</button>
            <button class="key number" data-num="6">6</button>
            <button class="key operator" data-op="-">−</button>
            <button class="key number" data-num="1">1</button>
            <button class="key number" data-num="2">2</button>
            <button class="key number" data-num="3">3</button>
            <button class="key operator" data-op="+">+</button>
            <button class="key number zero" data-num="0">0</button>
            <button class="key number" data-num=".">.</button>
            <button class="key equals" data-action="calculate">=</button>
        </div>
    </div>
    <script src="app.js"></script>
</body>
</html>`;

    const css = `:root {
    --bg: #0f172a;
    --card-bg: rgba(30, 41, 59, 0.7);
    --card-border: rgba(255, 255, 255, 0.1);
    --text-primary: #f8fafc;
    --text-muted: #94a3b8;
    --btn-num: rgba(255, 255, 255, 0.05);
    --btn-num-hover: rgba(255, 255, 255, 0.12);
    --btn-op: rgba(99, 102, 241, 0.2);
    --btn-op-hover: rgba(99, 102, 241, 0.35);
    --btn-equals: linear-gradient(135deg, #6366f1, #8b5cf6);
    --btn-equals-shadow: rgba(99, 102, 241, 0.4);
}
.light-theme {
    --bg: #f1f5f9;
    --card-bg: rgba(255, 255, 255, 0.8);
    --card-border: rgba(0, 0, 0, 0.08);
    --text-primary: #0f172a;
    --text-muted: #64748b;
    --btn-num: rgba(0, 0, 0, 0.04);
    --btn-num-hover: rgba(0, 0, 0, 0.08);
    --btn-op: rgba(99, 102, 241, 0.15);
    --btn-op-hover: rgba(99, 102, 241, 0.25);
}
* { box-sizing: border-box; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
body { background: var(--bg); min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; }
.calculator-card { background: var(--card-bg); backdrop-filter: blur(16px); border: 1px solid var(--card-border); border-radius: 28px; padding: 24px; width: 100%; max-width: 360px; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3); }
.calc-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.badge { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; color: #818cf8; background: rgba(99, 102, 241, 0.15); padding: 4px 10px; border-radius: 20px; }
.icon-btn { background: none; border: none; color: var(--text-muted); font-size: 1rem; cursor: pointer; margin-left: 8px; }
.display-container { background: rgba(0, 0, 0, 0.15); border-radius: 16px; padding: 18px; text-align: right; margin-bottom: 20px; min-height: 90px; display: flex; flex-direction: column; justify-content: flex-end; }
.history-display { font-size: 0.9rem; color: var(--text-muted); min-height: 1.2rem; }
.current-display { font-size: 2.2rem; font-weight: 700; color: var(--text-primary); }
.keypad { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.key { border: none; background: var(--btn-num); color: var(--text-primary); font-size: 1.2rem; padding: 16px; border-radius: 16px; cursor: pointer; }
.key.operator { background: var(--btn-op); color: #818cf8; }
.key.zero { grid-column: span 2; }
.key.equals { background: var(--btn-equals); color: #fff; box-shadow: 0 4px 15px var(--btn-equals-shadow); }`;

    const js = `let currentInput = '0', previousInput = '', currentOperation = null, resetScreen = false;
const currentDisplay = document.getElementById('currentDisplay');
const historyDisplay = document.getElementById('historyDisplay');

function updateDisplay() {
    if (currentDisplay) currentDisplay.textContent = currentInput;
    if (historyDisplay) historyDisplay.textContent = currentOperation ? \`\${previousInput} \${currentOperation}\` : '';
}
function appendNumber(num) {
    if (currentInput === '0' || resetScreen) { currentInput = num; resetScreen = false; }
    else { if (num === '.' && currentInput.includes('.')) return; currentInput += num; }
    updateDisplay();
}
function setOperation(op) {
    if (currentOperation !== null) calculate();
    previousInput = currentInput; currentOperation = op; resetScreen = true; updateDisplay();
}
function calculate() {
    if (currentOperation === null || resetScreen) return;
    const prev = parseFloat(previousInput), curr = parseFloat(currentInput);
    if (isNaN(prev) || isNaN(curr)) return;
    let res = 0;
    if (currentOperation === '+') res = prev + curr;
    if (currentOperation === '-') res = prev - curr;
    if (currentOperation === '*') res = prev * curr;
    if (currentOperation === '/') res = curr === 0 ? 'Error' : prev / curr;
    if (currentOperation === '%') res = prev % curr;
    currentInput = String(res); currentOperation = null; resetScreen = true; updateDisplay();
}
document.querySelectorAll('.key.number').forEach(b => b.addEventListener('click', () => appendNumber(b.dataset.num)));
document.querySelectorAll('.key.operator').forEach(b => b.addEventListener('click', () => setOperation(b.dataset.op)));
document.querySelector('.key.equals')?.addEventListener('click', calculate);
document.querySelectorAll('.key.action').forEach(b => {
    b.addEventListener('click', () => {
        if (b.dataset.action === 'clear') { currentInput = '0'; previousInput = ''; currentOperation = null; updateDisplay(); }
        if (b.dataset.action === 'delete') { currentInput = currentInput.slice(0, -1) || '0'; updateDisplay(); }
    });
});
console.log('[Calc] Calculator ready.');`;

    return {
        appTitle: "Glassmorphic Calculator",
        projectName: "Calculator",
        files: [
            { path: 'index.html', content: html },
            { path: 'styles.css', content: css },
            { path: 'app.js', content: js }
        ]
    };
}

function getGenericAppTemplate(userPrompt: string): TemplateResult {
    let clean = userPrompt
        .replace(/^(make|build|create|design|code|generate|please)\s+(me\s+)?(a\s+|an\s+)?/i, '')
        .replace(/\s+(repository|app|site|website|project|screen|page).*$/i, '')
        .trim();
    if (!clean) clean = 'Web Application';
    const appTitle = clean.length > 25 ? clean.substring(0, 22) + '...' : clean.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${appTitle}</title>
    <link rel="stylesheet" href="styles.css">
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
</head>
<body>
    <div class="app-layout">
        <header class="app-header">
            <div class="logo"><i class="fas fa-sparkles"></i> ${appTitle}</div>
            <button id="actionBtn" class="btn primary"><i class="fas fa-plus"></i> New Item</button>
        </header>
        <main class="app-content">
            <div class="stats-row">
                <div class="stat-card">
                    <span class="label">Total Generated</span>
                    <span id="counter" class="value">1</span>
                </div>
                <div class="stat-card">
                    <span class="label">Status</span>
                    <span class="value active"><i class="fas fa-circle-check"></i> Live</span>
                </div>
            </div>
            <div class="items-list" id="itemsList">
                <div class="item-card">
                    <i class="fas fa-rocket item-icon"></i>
                    <div class="item-info">
                        <h4>Welcome to your new app</h4>
                        <p>Built with AVI Web IDE. Edit and iterate in real-time!</p>
                    </div>
                </div>
            </div>
        </main>
    </div>
    <script src="app.js"></script>
</body>
</html>`;

    const css = `* { box-sizing: border-box; margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; }
body { background: #0b0f19; color: #f3f4f6; min-height: 100vh; display: flex; justify-content: center; padding: 24px; }
.app-layout { width: 100%; max-width: 600px; display: flex; flex-direction: column; gap: 20px; }
.app-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; }
.logo { font-size: 1.1rem; font-weight: 700; color: #818cf8; display: flex; align-items: center; gap: 8px; }
.btn.primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none; color: #fff; padding: 8px 16px; border-radius: 10px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.stats-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.stat-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 16px; }
.stat-card .label { font-size: 0.8rem; color: #9ca3af; display: block; margin-bottom: 4px; }
.stat-card .value { font-size: 1.4rem; font-weight: 700; }
.stat-card .value.active { color: #34d399; font-size: 1rem; display: flex; align-items: center; gap: 6px; }
.items-list { display: flex; flex-direction: column; gap: 10px; }
.item-card { display: flex; align-items: center; gap: 14px; padding: 16px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; }
.item-icon { font-size: 1.4rem; color: #818cf8; }
.item-info h4 { font-size: 0.95rem; margin-bottom: 2px; }
.item-info p { font-size: 0.8rem; color: #9ca3af; }`;

    const js = `let count = 1;
const btn = document.getElementById('actionBtn');
const list = document.getElementById('itemsList');
const counter = document.getElementById('counter');

btn?.addEventListener('click', () => {
    count++;
    if (counter) counter.textContent = count;
    const card = document.createElement('div');
    card.className = 'item-card';
    card.innerHTML = \`
        <i class="fas fa-check-circle item-icon" style="color: #34d399;"></i>
        <div class="item-info">
            <h4>Generated Item #\${count}</h4>
            <p>Added interactively at \${new Date().toLocaleTimeString()}</p>
        </div>
    \`;
    list?.prepend(card);
    console.log(\`[App] Item #\${count} added.\`);
});

console.log('AVI App Initialized successfully.');`;

    return {
        appTitle,
        projectName: appTitle,
        files: [
            { path: 'index.html', content: html },
            { path: 'styles.css', content: css },
            { path: 'app.js', content: js }
        ]
    };
}

function getGameTemplate(): TemplateResult {
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Cyber Frogger - Retro Arcade</title>
    <link rel="stylesheet" href="styles.css">
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
    <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Outfit:wght@400;600;800&display=swap" rel="stylesheet">
</head>
<body>
    <div class="arcade-cabinet">
        <header class="arcade-header">
            <div class="arcade-title">
                <i class="fas fa-gamepad" style="color: #38bdf8;"></i>
                <span>CYBER<strong>FROGGER</strong></span>
            </div>
            <div class="arcade-hud">
                <div class="hud-item"><span class="hud-lbl">SCORE</span><span id="scoreVal" class="hud-val">0000</span></div>
                <div class="hud-item"><span class="hud-lbl">HIGH</span><span id="highVal" class="hud-val">2500</span></div>
                <div class="hud-item"><span class="hud-lbl">LIVES</span><span id="livesContainer" class="hud-val lives-val">❤️❤️❤️</span></div>
            </div>
        </header>

        <div class="canvas-viewport">
            <canvas id="gameCanvas" width="600" height="600"></canvas>
            
            <div id="startOverlay" class="game-overlay">
                <h1 class="glitch-text">CYBER RUNNER</h1>
                <p>Cross the digital highway & neon river matrix!</p>
                <div class="controls-hint">
                    <span><i class="fas fa-arrow-up"></i><i class="fas fa-arrow-down"></i><i class="fas fa-arrow-left"></i><i class="fas fa-arrow-right"></i> Arrow Keys or WASD</span>
                </div>
                <button id="startBtn" class="arcade-btn"><i class="fas fa-play"></i> START GAME</button>
            </div>

            <div id="gameOverOverlay" class="game-overlay" style="display: none;">
                <h1 style="color: #ef4444;">GAME OVER</h1>
                <p>Final Score: <span id="finalScoreVal" style="color: #38bdf8; font-weight: 800;">0</span></p>
                <button id="restartBtn" class="arcade-btn"><i class="fas fa-rotate-left"></i> TRY AGAIN</button>
            </div>
        </div>

        <div class="mobile-touch-controls">
            <div class="dpad-row"><button class="dpad-btn" id="btnUp"><i class="fas fa-arrow-up"></i></button></div>
            <div class="dpad-row middle">
                <button class="dpad-btn" id="btnLeft"><i class="fas fa-arrow-left"></i></button>
                <button class="dpad-btn center-act" id="btnAction"><i class="fas fa-bolt"></i></button>
                <button class="dpad-btn" id="btnRight"><i class="fas fa-arrow-right"></i></button>
            </div>
            <div class="dpad-row"><button class="dpad-btn" id="btnDown"><i class="fas fa-arrow-down"></i></button></div>
        </div>
    </div>
    <script src="app.js"></script>
</body>
</html>`;

    const css = `* { box-sizing: border-box; margin: 0; padding: 0; user-select: none; }
body {
    background: #090a0f;
    color: #e2e8f0;
    font-family: 'Outfit', sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 12px;
    overflow: hidden;
}

.arcade-cabinet {
    width: 100%;
    max-width: 640px;
    background: #11131c;
    border: 2px solid rgba(56, 189, 248, 0.3);
    border-radius: 16px;
    box-shadow: 0 0 30px rgba(56, 189, 248, 0.15), 0 20px 50px rgba(0,0,0,0.8);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.arcade-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 18px;
    background: #0a0b10;
    border-bottom: 2px solid rgba(255, 255, 255, 0.08);
}

.arcade-title {
    font-family: 'Press Start 2P', monospace;
    font-size: 0.85rem;
    color: #38bdf8;
    display: flex;
    align-items: center;
    gap: 8px;
}
.arcade-title strong { color: #a855f7; }

.arcade-hud {
    display: flex;
    gap: 16px;
}

.hud-item {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.hud-lbl {
    font-size: 0.65rem;
    color: #94a3b8;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.hud-val {
    font-family: 'Press Start 2P', monospace;
    font-size: 0.85rem;
    color: #4ade80;
}
.lives-val { font-family: sans-serif; font-size: 0.85rem; }

.canvas-viewport {
    position: relative;
    width: 100%;
    background: #050608;
    display: flex;
    align-items: center;
    justify-content: center;
}

#gameCanvas {
    width: 100%;
    max-width: 600px;
    height: auto;
    aspect-ratio: 1 / 1;
    display: block;
    image-rendering: pixelated;
    background: #07090e;
}

.game-overlay {
    position: absolute;
    inset: 0;
    background: rgba(9, 10, 15, 0.88);
    backdrop-filter: blur(8px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    text-align: center;
    padding: 24px;
}

.game-overlay h1 {
    font-family: 'Press Start 2P', monospace;
    font-size: 1.4rem;
    color: #38bdf8;
    letter-spacing: -0.5px;
}

.game-overlay p {
    font-size: 0.9rem;
    color: #cbd5e1;
}

.controls-hint {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 6px 14px;
    border-radius: 9999px;
    font-size: 0.75rem;
    color: #94a3b8;
}

.arcade-btn {
    font-family: 'Press Start 2P', monospace;
    font-size: 0.8rem;
    padding: 12px 24px;
    background: linear-gradient(135deg, #0284c7, #38bdf8);
    border: none;
    border-radius: 8px;
    color: #ffffff;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(56, 189, 248, 0.4);
    transition: transform 0.1s ease, filter 0.2s ease;
}
.arcade-btn:hover { filter: brightness(1.15); transform: translateY(-2px); }
.arcade-btn:active { transform: translateY(1px); }

/* Virtual Touch D-Pad */
.mobile-touch-controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px;
    background: #0a0b10;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.dpad-row {
    display: flex;
    gap: 12px;
}
.dpad-row.middle {
    margin: 4px 0;
}

.dpad-btn {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #e2e8f0;
    border-radius: 10px;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}
.dpad-btn:active {
    background: #38bdf8;
    color: #090a0f;
}
.dpad-btn.center-act {
    background: rgba(56, 189, 248, 0.2);
    border-color: rgba(56, 189, 248, 0.4);
    color: #38bdf8;
}`;

    const js = `// Cyber Frogger Arcade Game Engine
const canvas = document.getElementById('gameCanvas');
const ctx = canvas.getContext('2d');

const scoreVal = document.getElementById('scoreVal');
const highVal = document.getElementById('highVal');
const livesContainer = document.getElementById('livesContainer');
const startOverlay = document.getElementById('startOverlay');
const gameOverOverlay = document.getElementById('gameOverOverlay');
const finalScoreVal = document.getElementById('finalScoreVal');
const startBtn = document.getElementById('startBtn');
const restartBtn = document.getElementById('restartBtn');

const GRID_SIZE = 40;
const COLS = 15;
const ROWS = 15;

let isPlaying = false;
let score = 0;
let highScore = 2500;
let lives = 3;
let animationId = null;

// Sound synthesizer using Web Audio API
const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
function playBeep(freq = 440, duration = 0.08, type = 'square') {
    try {
        if (audioCtx.state === 'suspended') audioCtx.resume();
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(freq, audioCtx.currentTime);
        gain.gain.setValueAtTime(0.1, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + duration);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start();
        osc.stop(audioCtx.currentTime + duration);
    } catch(e) {}
}

const player = {
    x: 7 * GRID_SIZE,
    y: 13 * GRID_SIZE,
    size: GRID_SIZE - 4,
    color: '#34d399',
    reset() {
        this.x = 7 * GRID_SIZE;
        this.y = 13 * GRID_SIZE;
    }
};

// Lanes configuration
const lanes = [
    { y: 1 * GRID_SIZE, type: 'goal', items: [0, 3, 6, 9, 12].map(c => ({ x: c * GRID_SIZE, filled: false })) },
    { y: 2 * GRID_SIZE, type: 'river', speed: 1.5, w: 90, color: '#38bdf8', items: [{x: 0}, {x: 200}, {x: 400}] },
    { y: 3 * GRID_SIZE, type: 'river', speed: -2.2, w: 120, color: '#0284c7', items: [{x: 50}, {x: 280}, {x: 500}] },
    { y: 4 * GRID_SIZE, type: 'river', speed: 1.8, w: 80, color: '#38bdf8', items: [{x: 20}, {x: 240}, {x: 460}] },
    { y: 5 * GRID_SIZE, type: 'river', speed: -1.2, w: 100, color: '#0284c7', items: [{x: 100}, {x: 350}] },
    { y: 6 * GRID_SIZE, type: 'safe', color: '#1e293b' },
    { y: 7 * GRID_SIZE, type: 'road', speed: -2.0, w: 50, color: '#f43f5e', items: [{x: 100}, {x: 300}, {x: 500}] },
    { y: 8 * GRID_SIZE, type: 'road', speed: 2.8, w: 60, color: '#fbbf24', items: [{x: 50}, {x: 250}, {x: 450}] },
    { y: 9 * GRID_SIZE, type: 'road', speed: -3.2, w: 50, color: '#f43f5e', items: [{x: 0}, {x: 220}, {x: 440}] },
    { y: 10 * GRID_SIZE, type: 'road', speed: 1.6, w: 80, color: '#a855f7', items: [{x: 80}, {x: 340}] },
    { y: 11 * GRID_SIZE, type: 'road', speed: -2.4, w: 50, color: '#fbbf24', items: [{x: 40}, {x: 260}, {x: 480}] },
    { y: 12 * GRID_SIZE, type: 'safe', color: '#1e293b' },
    { y: 13 * GRID_SIZE, type: 'safe', color: '#0f172a' }
];

function movePlayer(dx, dy) {
    if (!isPlaying) return;
    const nx = player.x + dx * GRID_SIZE;
    const ny = player.y + dy * GRID_SIZE;

    if (nx >= 0 && nx <= (COLS - 1) * GRID_SIZE) player.x = nx;
    if (ny >= 0 && ny <= (ROWS - 1) * GRID_SIZE) {
        if (dy < 0) {
            score += 10;
            scoreVal.textContent = String(score).padStart(4, '0');
        }
        player.y = ny;
    }
    playBeep(520, 0.05, 'triangle');
}

window.addEventListener('keydown', e => {
    if (e.key === 'ArrowUp' || e.key === 'w') movePlayer(0, -1);
    if (e.key === 'ArrowDown' || e.key === 's') movePlayer(0, 1);
    if (e.key === 'ArrowLeft' || e.key === 'a') movePlayer(-1, 0);
    if (e.key === 'ArrowRight' || e.key === 'd') movePlayer(1, 0);
});

// Mobile button listeners
document.getElementById('btnUp')?.addEventListener('click', () => movePlayer(0, -1));
document.getElementById('btnDown')?.addEventListener('click', () => movePlayer(0, 1));
document.getElementById('btnLeft')?.addEventListener('click', () => movePlayer(-1, 0));
document.getElementById('btnRight')?.addEventListener('click', () => movePlayer(1, 0));

function update() {
    if (!isPlaying) return;

    let onLog = false;
    let inRiver = false;

    lanes.forEach(lane => {
        if (lane.speed && lane.items) {
            lane.items.forEach(item => {
                item.x += lane.speed;
                if (lane.speed > 0 && item.x > canvas.width) item.x = -lane.w;
                if (lane.speed < 0 && item.x < -lane.w) item.x = canvas.width;

                // Check collision with road vehicle
                if (lane.type === 'road' && player.y === lane.y) {
                    if (player.x + player.size > item.x && player.x < item.x + lane.w) {
                        die();
                    }
                }

                // Check boarding river energy log
                if (lane.type === 'river' && player.y === lane.y) {
                    inRiver = true;
                    if (player.x + player.size > item.x && player.x < item.x + lane.w) {
                        onLog = true;
                        player.x += lane.speed;
                    }
                }
            });
        }

        // Check goal zone
        if (lane.type === 'goal' && player.y === lane.y) {
            lane.items.forEach(goal => {
                if (Math.abs(player.x - goal.x) < 20 && !goal.filled) {
                    goal.filled = true;
                    score += 250;
                    scoreVal.textContent = String(score).padStart(4, '0');
                    playBeep(880, 0.15, 'sine');
                    player.reset();
                }
            });
        }
    });

    if (inRiver && !onLog) {
        die();
    }

    if (player.x < 0 || player.x > canvas.width - player.size) {
        die();
    }
}

function die() {
    lives--;
    playBeep(180, 0.25, 'sawtooth');
    livesContainer.textContent = '❤️'.repeat(Math.max(0, lives));
    player.reset();

    if (lives <= 0) {
        isPlaying = false;
        if (score > highScore) {
            highScore = score;
            highVal.textContent = String(highScore).padStart(4, '0');
        }
        finalScoreVal.textContent = score;
        gameOverOverlay.style.display = 'flex';
    }
}

function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw lanes
    lanes.forEach(lane => {
        if (lane.type === 'river') {
            ctx.fillStyle = '#0369a1';
            ctx.fillRect(0, lane.y, canvas.width, GRID_SIZE);
            lane.items?.forEach(item => {
                ctx.fillStyle = lane.color;
                ctx.shadowColor = lane.color;
                ctx.shadowBlur = 10;
                ctx.fillRect(item.x, lane.y + 4, lane.w, GRID_SIZE - 8);
                ctx.shadowBlur = 0;
            });
        } else if (lane.type === 'road') {
            ctx.fillStyle = '#1e293b';
            ctx.fillRect(0, lane.y, canvas.width, GRID_SIZE);
            lane.items?.forEach(item => {
                ctx.fillStyle = lane.color;
                ctx.shadowColor = lane.color;
                ctx.shadowBlur = 12;
                ctx.fillRect(item.x, lane.y + 6, lane.w, GRID_SIZE - 12);
                ctx.shadowBlur = 0;
            });
        } else if (lane.type === 'goal') {
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(0, lane.y, canvas.width, GRID_SIZE);
            lane.items?.forEach(goal => {
                ctx.fillStyle = goal.filled ? '#34d399' : '#334155';
                ctx.fillRect(goal.x, lane.y + 4, GRID_SIZE, GRID_SIZE - 8);
            });
        } else {
            ctx.fillStyle = lane.color || '#090a0f';
            ctx.fillRect(0, lane.y, canvas.width, GRID_SIZE);
        }
    });

    // Draw Player
    ctx.fillStyle = player.color;
    ctx.shadowColor = '#34d399';
    ctx.shadowBlur = 15;
    ctx.fillRect(player.x + 2, player.y + 2, player.size, player.size);
    ctx.shadowBlur = 0;
}

function gameLoop() {
    update();
    draw();
    if (isPlaying) {
        animationId = requestAnimationFrame(gameLoop);
    }
}

function startGame() {
    score = 0;
    lives = 3;
    scoreVal.textContent = '0000';
    livesContainer.textContent = '❤️❤️❤️';
    player.reset();
    startOverlay.style.display = 'none';
    gameOverOverlay.style.display = 'none';
    isPlaying = true;
    playBeep(659, 0.1, 'sine');
    gameLoop();
}

startBtn?.addEventListener('click', startGame);
restartBtn?.addEventListener('click', startGame);
draw();`;

    return {
        appTitle: 'Cyber Frogger Game',
        projectName: 'Cyber Frogger Game',
        files: [
            { path: 'index.html', content: html },
            { path: 'styles.css', content: css },
            { path: 'app.js', content: js }
        ]
    };
}
